import { MAIN_IDB_STORE } from '../util/browser/idb';
import { getGlobal } from '../global';
import { isCentralDbEnabled } from '../rasitech/centralDb/config';
import { scheduleForensicSnapshotPush } from '../rasitech/centralDb/sync';
import { scheduleCatalogSync } from '../rasitech/centralDb/syncCatalog';
import { shouldPauseRasiBackgroundWork } from '../rasitech/rasitechPerf';
import type {
  ForensicDatabase,
  ForensicEntity,
  ForensicEntityLink,
  ForensicMedia,
  ForensicMessage,
  ForensicPeer,
  ForensicPeerHistory,
} from './types';
import { EMPTY_FORENSIC_DB } from './types';

const FORENSIC_DB_KEY = 'rasi-forensic-db-v1';

let memoryDb: ForensicDatabase = { ...EMPTY_FORENSIC_DB };
let loadPromise: Promise<void> | undefined;

export async function loadForensicDb(): Promise<ForensicDatabase> {
  if (!loadPromise) {
    loadPromise = (async () => {
      const stored = await MAIN_IDB_STORE.get<ForensicDatabase>(FORENSIC_DB_KEY);
      if (stored?.version) {
        memoryDb = {
          ...EMPTY_FORENSIC_DB,
          ...stored,
          peers: stored.peers || {},
          messages: stored.messages || {},
          entities: stored.entities || {},
          entityLinks: stored.entityLinks || [],
          peerHistory: stored.peerHistory || [],
          media: stored.media || {},
          config: stored.config || {},
        };
      }
    })();
  }
  await loadPromise;
  return memoryDb;
}

export function getForensicDb(): ForensicDatabase {
  return memoryDb;
}

let saveTimer: number | undefined;

export function scheduleForensicSave() {
  if (saveTimer) {
    window.clearTimeout(saveTimer);
  }
  saveTimer = window.setTimeout(() => {
    saveTimer = undefined;
    void MAIN_IDB_STORE.set(FORENSIC_DB_KEY, memoryDb);
    if (isCentralDbEnabled() && !shouldPauseRasiBackgroundWork(getGlobal())) {
      scheduleForensicSnapshotPush(() => ({
        version: memoryDb.version,
        messageCount: Object.keys(memoryDb.messages).length,
        entityCount: Object.keys(memoryDb.entities).length,
        peerCount: Object.keys(memoryDb.peers).length,
        linkCount: memoryDb.entityLinks.length,
        messages: Object.values(memoryDb.messages).slice(-80),
        peers: Object.fromEntries(Object.entries(memoryDb.peers).slice(0, 80)),
        entities: Object.values(memoryDb.entities).slice(-80),
        entityLinks: memoryDb.entityLinks.slice(-60),
      }));
      scheduleCatalogSync();
    }
  }, 800);
}

export function upsertPeer(peer: ForensicPeer, trackHistory = true) {
  const existing = memoryDb.peers[peer.id];
  if (existing && trackHistory) {
    (['title', 'username', 'phone', 'bio', 'photoId'] as const).forEach((field) => {
      const oldVal = existing[field];
      const newVal = peer[field];
      if (oldVal !== newVal && (oldVal || newVal)) {
        memoryDb.peerHistory.push({
          id: memoryDb.nextHistoryId++,
          peerId: peer.id,
          field,
          oldValue: oldVal,
          newValue: newVal,
          recordedAt: Date.now(),
        });
      }
    });
  }
  memoryDb.peers[peer.id] = { ...existing, ...peer, updatedAt: Date.now() };
  scheduleForensicSave();
}

export function upsertMessage(message: ForensicMessage) {
  const key = `${message.chatId}_${message.messageId}`;
  memoryDb.messages[key] = message;
  scheduleForensicSave();
}

export function linkEntities(
  entities: ForensicEntity[],
  chatId: string,
  messageId: number,
  context?: string,
) {
  entities.forEach((entity) => {
    memoryDb.entities[entity.id] = entity;
    memoryDb.entityLinks.push({
      id: memoryDb.nextLinkId++,
      entityId: entity.id,
      chatId,
      messageId,
      context: context?.slice(0, 200),
    });
  });
  scheduleForensicSave();
}

export function upsertMedia(media: ForensicMedia) {
  memoryDb.media[media.id] = media;
  scheduleForensicSave();
}

export function setForensicConfig(key: string, value: string) {
  memoryDb.config[key] = value;
  scheduleForensicSave();
}

export async function clearForensicDb() {
  memoryDb = { ...EMPTY_FORENSIC_DB };
  await MAIN_IDB_STORE.set(FORENSIC_DB_KEY, memoryDb);
}
